import { useNavigate } from 'react-router';

import { datadogLogs } from '@datadog/browser-logs';
import { IconLanguage, IconPrinter, IconShare2 } from '@tabler/icons-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useTrackResourcePrint } from '@/hooks/useResourcePrintTracking';

import { printTridionDocument } from '@/utils/print';
import { cn } from '@/utils/tailwind';

import { Header } from '@/components/header';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { routes } from '@/constants';

import {
  documentAtom,
  isDirectLinkRoutedFrom,
  isTridionDocAtom,
  toggleTranslationAtom,
  translatedDocumentNameAtom,
  useBackUrl,
  useCategoryName,
  useRoutedFrom,
} from '@/atoms/document';
import { searchAtom } from '@/atoms/search';

interface DocumentHeaderProps {
  name?: string;
  planName?: string;
  planId?: string;
  isTrainTogetherMode?: boolean;
  isTridion?: boolean;
}

const DocumentHeader: React.FC<DocumentHeaderProps> = ({
  name,
  planName,
  planId,
  isTrainTogetherMode,
  isTridion: isTridionProp,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const trackResourcePrint = useTrackResourcePrint();
  const { terms } = useAtomValue(searchAtom);
  const searchTerm = terms[0];
  const { hasTranslatedValue } = useAtomValue(documentAtom);
  const isTridionFromState = useAtomValue(isTridionDocAtom);
  const [routedFrom] = useRoutedFrom();
  const [backUrl] = useBackUrl();
  const [categoryName] = useCategoryName();
  const toggleTranslation = useSetAtom(toggleTranslationAtom);
  const docName = useAtomValue(translatedDocumentNameAtom);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {t('Generic.linkCopied')}
        </ToastMessageBlock>
      ));
    } catch (error) {
      datadogLogs.logger.warn('clipboard_write_failed', {
        error: error instanceof Error ? error.message : String(error),
        source: 'DocumentHeader',
      });
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {`${t('GenericError.error')}: ${t('GenericError.tryAgain')}`}
        </ToastMessageBlock>
      ));
    }
  };

  const isTridion = !!isTridionProp || isTridionFromState;

  const isSearch = routedFrom === 'search';

  const goBack = () => {
    if (isTrainTogetherMode) {
      navigate(-1);
    } else if (planId) {
      navigate(routes.plans.plan({ planId }));
    } else if (isDirectLinkRoutedFrom(routedFrom) && !isSearch) {
      navigate('/');
    } else if (backUrl) {
      navigate(backUrl);
    } else if (!categoryName && searchTerm) {
      navigate(`/search?query=${searchTerm}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <Header.Root className={cn(isTrainTogetherMode && 'top-0! left-0! m-2!')}>
      <Header.Title>{docName ?? name}</Header.Title>
      <Header.Back as="button" onClick={goBack}>
        {planName ??
          (isDirectLinkRoutedFrom(routedFrom) && !isSearch
            ? t('Generic.explore')
            : categoryName
              ? categoryName
              : t('Generic.search'))}
      </Header.Back>
      {isTridion && !isTrainTogetherMode && (
        <Header.ActionIconButton
          icon={IconLanguage}
          disabled={!hasTranslatedValue}
          tooltip="Generic.translate"
          onClick={toggleTranslation}
        />
      )}
      {isTridion && !isTrainTogetherMode && (
        <Header.ActionIconButton
          icon={IconPrinter}
          tooltip="Button.print"
          onClick={() => {
            trackResourcePrint('button', name);
            printTridionDocument();
          }}
        />
      )}
      {isTridion && !isTrainTogetherMode && (
        <Header.ActionIconButton
          icon={IconShare2}
          tooltip="Button.copy"
          onClick={() => {
            void copyLink();
          }}
        />
      )}
    </Header.Root>
  );
};

export default DocumentHeader;
